import React, { useCallback, useContext, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'

import CityPage from 'Containers/City/Page'
import { SnackBarContext } from 'Components/SnackBar'
import HoldSettingPrompt from 'Components/PowerBanks/HoldSettingPrompt'
import HoldSettingCard from 'Components/PowerBanks/HoldSettingCard'
import {
  useCityCurrency,
  usePowerBankHoldSetting,
} from 'Components/PowerBanks/hooks'
import { formatHoldAmount } from 'Utils/PowerBanks'

import { InventoryWrapper } from './components/InventoryWrapper'
import Table from './components/Table'
import AddModal from './components/AddModal'
import EditModal from './components/EditModal'
import DeleteModal from './components/DeleteModal'
import ImportPowerBanksModal from './components/ImportPowerBanksModal'

const Inventory = props => {
  const { cityName } = props
  const { cityId } = useParams()
  const tableRef = useRef()
  const runSnackBar = useContext(SnackBarContext)
  const [activeModal, setActiveModal] = useState({})
  const [holdPromptOpen, setHoldPromptOpen] = useState(false)
  const { money } = useCityCurrency(cityId)
  const {
    setting: holdSetting,
    setSetting: setHoldSetting,
    loading: holdSettingLoading,
  } = usePowerBankHoldSetting(cityId)

  const onShowModal = useCallback(
    (name, data) => setActiveModal({ name, data }),
    [],
  )

  const onHideModal = useCallback(() => setActiveModal({}), [])

  const onDeleteModalConfirm = useCallback(() => {
    tableRef.current.reloadData()
    onHideModal()
  }, [])

  const onEditModalConfirm = useCallback(() => {
    tableRef.current.reloadData()
    onHideModal()
  }, [])

  const onAddModalConfirm = useCallback(() => {
    tableRef.current.reloadData()
    onHideModal()
  }, [])

  /**
   * The CSV is in. Reload the table, tell the manager how many units were
   * created, then ask the hold question - that answer is the city default.
   */
  const onImportPowerBanksConfirm = useCallback(
    ({ createdCount }) => {
      tableRef.current.reloadData()
      onHideModal()
      runSnackBar({
        type: 'success',
        msg: `${createdCount} power bank${
          createdCount === 1 ? '' : 's'
        } added to ${cityName || 'this city'}`,
      })
      setHoldPromptOpen(true)
    },
    [cityName, runSnackBar, onHideModal],
  )

  const onHoldSettingSaved = useCallback(
    saved => {
      setHoldPromptOpen(false)
      setHoldSetting(saved)
      runSnackBar({
        type: 'success',
        msg: saved.holdRequired
          ? `Hold saved: ${formatHoldAmount(saved.holdAmount, {
              currencySign: money.currencySign,
              currency: saved.currency,
            })} per power bank`
          : 'Saved: no hold for power banks in this city',
      })
    },
    [money, runSnackBar, setHoldSetting],
  )

  const onHoldSettingCancel = useCallback(() => {
    setHoldPromptOpen(false)
    runSnackBar({
      type: 'info',
      msg: 'Hold setting left unchanged',
    })
  }, [runSnackBar])

  return (
    <CityPage
      cityId={cityId}
      cityName={cityName}
      breadcrumbs={[{ label: 'Inventory' }]}
    >
      <InventoryWrapper>
        <HoldSettingCard
          setting={holdSetting}
          money={money}
          loading={holdSettingLoading}
          onChange={() => setHoldPromptOpen(true)}
        />
        <Table showModal={onShowModal} ref={tableRef} />
        {activeModal.name === 'add' && (
          <AddModal
            cityId={cityId}
            handleConfirm={onAddModalConfirm}
            handleCancel={onHideModal}
          />
        )}
        {activeModal.name === 'edit' && (
          <EditModal
            cityId={cityId}
            item={activeModal.data}
            handleConfirm={onEditModalConfirm}
            handleCancel={onHideModal}
          />
        )}
        {activeModal.name === 'delete' && (
          <DeleteModal
            cityId={cityId}
            item={activeModal.data}
            handleConfirm={onDeleteModalConfirm}
            handleCancel={onHideModal}
          />
        )}
        {activeModal.name === 'importPowerBanks' && (
          <ImportPowerBanksModal
            cityId={cityId}
            handleConfirm={onImportPowerBanksConfirm}
            handleCancel={onHideModal}
          />
        )}
        {holdPromptOpen && (
          <HoldSettingPrompt
            show
            cityId={cityId}
            money={money}
            currentSetting={holdSetting}
            onSaved={onHoldSettingSaved}
            onCancel={onHoldSettingCancel}
          />
        )}
      </InventoryWrapper>
    </CityPage>
  )
}

export default Inventory
